// ── app.jsx ──
const { useState, useEffect, useRef, useCallback } = React;

const API_BASE = '';

// ── API ───────────────────────────────────────────────────────────────────────

async function api(method, path, body) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const res = await fetch(`${API_BASE}${path}`, opts);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
}

// ── Styles ───────────────────────────────────────────────────────────────────

const C = {
  bg:       '#07141a',
  panel:    '#0d1f28',
  card:     '#0a1c24',
  border:   '#1e3a45',
  border2:  '#152d38',
  text:     '#E8EDF2',
  muted:    '#8A9097',
  accent:   '#8ED11B',
  accentDk: '#6ba312',
  red:      '#ef4444',
  amber:    '#f59e0b',
  green:    '#10b981',
  blue:     '#3b82f6',
  purple:   '#a855f7',
  gold:     '#f59e0b',
};

const s = {
  input: {
    background: '#0a1c24',
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    padding: '10px 12px',
    fontSize: 14,
    width: '100%',
    outline: 'none',
  },
  btn: (variant = 'primary') => ({
    padding: '10px 20px',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'inherit',
    background: variant === 'primary' ? C.accent
              : variant === 'danger'  ? C.red
              : variant === 'ghost'   ? 'transparent'
              : C.border,
    color: variant === 'ghost' ? C.muted : variant === 'secondary' ? C.text : '#07141a',
    border: variant === 'ghost' ? `1px solid ${C.border}` : 'none',
  }),
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: C.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    display: 'block',
    marginBottom: 6,
  },
};

// ── Shared Components ─────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        border: `3px solid ${C.border}`, borderTopColor: C.accent,
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Chip({ label, color = C.border, textColor = C.text }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      background: color + '33',
      color: color,
      border: `1px solid ${color}55`,
      whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function Alert({ type = 'error', children }) {
  const bg = type === 'error' ? '#ef444422' : '#f59e0b22';
  const border = type === 'error' ? '#ef444455' : '#f59e0b55';
  const color = type === 'error' ? '#fca5a5' : '#fcd34d';
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 6, padding: '10px 14px', color, fontSize: 13 }}>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000000bb',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: C.panel, border: `1px solid ${C.border}`,
        borderRadius: 10, padding: 28, width: 480, maxWidth: '90vw',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Permissions ───────────────────────────────────────────────────────────────

const PERM_GROUPS = [
  {
    label: 'Always On',
    locked: true,
    perms: [
      { key: 'lookup',       label: 'Package Lookup'  },
      { key: 'view_history', label: 'View History'    },
    ],
  },
  {
    label: 'Package Operations',
    perms: [
      { key: 'adjustment', label: 'Package Adjustments'    },
      { key: 'combine',  label: 'Combine / Create Packages' },
      { key: 'location', label: 'Move Locations'          },
    ],
  },
  {
    label: 'Transfers',
    perms: [
      { key: 'transfers_out', label: 'Create Outgoing Transfers'   },
      { key: 'transfers_in',  label: 'Manage Incoming Transfers'   },
    ],
  },
  {
    label: 'Plant Operations',
    perms: [
      { key: 'plant_batches', label: 'Plant Batches & Growth Phase' },
      { key: 'harvest',       label: 'Harvest & Package from Harvest' },
      { key: 'plant_waste',   label: 'Waste / Destroy Plants & Batches' },
      { key: 'plant_location', label: 'Move Plants & Batches' },
    ],
  },
  {
    label: 'Admin',
    perms: [
      { key: 'manage_workers',     label: 'Manage Workers'    },
      { key: 'manage_devices',     label: 'Manage Devices'    },
      { key: 'manage_permissions', label: 'Grant Permissions' },
    ],
  },
];

function PermissionsModal({ worker, session, activeFarm, onClose }) {
  const [perms, setPerms]   = useState(null);
  const [direct, setDirect] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  useEffect(() => {
    api('GET', `/workers/${worker.id}/permissions?farm_license=${encodeURIComponent(activeFarm.license)}`)
      .then(d => { setPerms(d); setDirect(new Set(d.direct || [])); })
      .catch(e => setErr(e.message));
  }, [worker.id]);

  function toggle(key) {
    if (!perms || perms.is_owner || (perms.from_roles && perms.from_roles[key])) return;
    setDirect(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function save() {
    setSaving(true); setErr('');
    try {
      await api('PUT', `/workers/${worker.id}/permissions`, {
        farm_license: activeFarm.license,
        permissions: [...direct],
        account_id: session.account_id,
      });
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={`Permissions — ${worker.name}`} onClose={onClose}>
      {perms === null ? <Spinner /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {perms.roles && perms.roles.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                Roles
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {perms.roles.map(r => (
                  <Chip key={r.id} label={r.name} color={r.is_system ? C.gold : C.accent} />
                ))}
              </div>
            </div>
          )}

          {perms.is_owner && (
            <div style={{ background: '#8ED11B18', border: `1px solid #8ED11B44`, borderRadius: 6, padding: '8px 12px', fontSize: 12, color: C.accent }}>
              Owner — all permissions granted via the Owner role
            </div>
          )}

          {PERM_GROUPS.map(group => (
            <div key={group.label}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                {group.label}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {group.perms.map(p => {
                  const roleSource = !group.locked && perms.from_roles && perms.from_roles[p.key];
                  const fromRole   = !!roleSource;
                  const on         = group.locked || fromRole || direct.has(p.key);
                  const disabled   = group.locked || perms.is_owner || fromRole;
                  return (
                    <label key={p.key} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 10px', borderRadius: 6,
                      background: on ? C.accent + '12' : C.bg,
                      border: `1px solid ${on ? C.accent + '40' : C.border}`,
                      cursor: disabled ? 'default' : 'pointer',
                      opacity: disabled ? 0.7 : 1,
                    }}>
                      <input
                        type="checkbox" checked={on} disabled={disabled}
                        onChange={() => toggle(p.key)}
                        style={{ accentColor: C.accent, width: 14, height: 14 }}
                      />
                      <span style={{ fontSize: 13, color: on ? C.text : C.muted, flex: 1 }}>{p.label}</span>
                      {group.locked && <span style={{ fontSize: 10, color: C.muted }}>always on</span>}
                      {fromRole && <span style={{ fontSize: 10, color: C.accent, fontWeight: 600 }}>via {roleSource}</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}

          {err && <Alert>{err}</Alert>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
            <button onClick={onClose} style={s.btn('ghost')}>Cancel</button>
            {!perms.is_owner && (
              <button onClick={save} disabled={saving} style={{ ...s.btn('primary'), opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Auth View ─────────────────────────────────────────────────────────────────

function AuthView({ onSession }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const body = mode === 'login' ? { email, password } : { email, username, password };
      const data = await api('POST', mode === 'login' ? '/auth/login' : '/auth/register', body);
      onSession(data, mode === 'register');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ width: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <a href="/" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 4, color: C.text, textDecoration: 'none', display: 'block' }}>
            Terra<span style={{ color: C.accent }}>Soft</span>
          </a>
          <div style={{ color: C.muted, fontSize: 14 }}>Sign in to your TerraSoft account</div>
        </div>

        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 28 }}>
          <div style={{ display: 'flex', marginBottom: 24, background: C.bg, borderRadius: 6, padding: 4 }}>
            {['login', 'register'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); }}
                style={{ flex: 1, padding: '8px 0', borderRadius: 4, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                  background: mode === m ? C.accent : 'transparent',
                  color: mode === m ? '#07141a' : C.muted,
                }}>
                {m === 'login' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>

          <form onSubmit={submit}>
            <div style={{ marginBottom: 16 }}>
              <label style={s.label}>Email</label>
              <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="owner@farm.com" required autoFocus />
            </div>
            {mode === 'register' && (
              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>Username</label>
                <input style={s.input} value={username} onChange={e => setUsername(e.target.value)} placeholder="Used to log in to TerraLink" required minLength={2} />
              </div>
            )}
            <div style={{ marginBottom: mode === 'register' ? 16 : 20 }}>
              <label style={s.label}>Password</label>
              <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={mode === 'register' ? 8 : 1} />
            </div>
            {mode === 'register' && (
              <div style={{ marginBottom: 20 }}>
                <label style={s.label}>Confirm Password</label>
                <input style={s.input} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" required minLength={8} />
              </div>
            )}

            {error && <div style={{ marginBottom: 16 }}><Alert>{error}</Alert></div>}
            {forgotSent && <div style={{ marginBottom: 16 }}><Alert type="warning">If that email exists, a reset link is on its way.</Alert></div>}

            <button type="submit" disabled={loading} style={{ ...s.btn('primary'), width: '100%', opacity: loading ? 0.6 : 1 }}>
              {loading ? '…' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>

            {mode === 'login' && (
              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <button type="button" onClick={async () => {
                  if (!email) { setError('Enter your email above first.'); return; }
                  setLoading(true); setError('');
                  try {
                    await api('POST', '/auth/forgot-password', { email });
                    setForgotSent(true);
                  } catch { setForgotSent(true); }
                  finally { setLoading(false); }
                }} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
                  Forgot password?
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Verify Email View ─────────────────────────────────────────────────────────

function VerifyEmailView({ session, onVerified, onLogout }) {
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [error, setError] = useState('');

  async function checkVerification() {
    setChecking(true); setError('');
    try {
      const data = await api('GET', `/auth/check?account_id=${session.account_id}`);
      if (data.is_verified) {
        onVerified();
      } else {
        setError('Email not verified yet. Check your inbox and click the link first.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setChecking(false);
    }
  }

  async function resendVerification() {
    setResending(true); setError(''); setResendSent(false);
    try {
      await api('POST', '/auth/resend-verification', { account_id: session.account_id });
      setResendSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ width: 420, textAlign: 'center' }}>
        <a href="/" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 32, color: C.text, textDecoration: 'none', display: 'block' }}>
          Terra<span style={{ color: C.accent }}>Soft</span>
        </a>

        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 36 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✉</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 10 }}>Check your email</div>
          <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, marginBottom: 8 }}>
            We sent a verification link to
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.accent, marginBottom: 24 }}>{session.email}</div>
          <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 28 }}>
            Click the link in the email, then come back here and click the button below.
          </div>

          {error && <div style={{ marginBottom: 16 }}><Alert>{error}</Alert></div>}
          {resendSent && (
            <div style={{ marginBottom: 16, background: '#8ED11B18', border: '1px solid #8ED11B44', borderRadius: 6, padding: '10px 14px', color: C.accent, fontSize: 13 }}>
              Verification email resent. Check your inbox.
            </div>
          )}

          <button onClick={checkVerification} disabled={checking}
            style={{ ...s.btn('primary'), width: '100%', opacity: checking ? 0.6 : 1, marginBottom: 12 }}>
            {checking ? 'Checking…' : "I've verified my email — Continue"}
          </button>

          <button onClick={resendVerification} disabled={resending}
            style={{ ...s.btn('ghost'), width: '100%', opacity: resending ? 0.6 : 1, marginBottom: 12 }}>
            {resending ? 'Sending…' : 'Resend verification email'}
          </button>

          <button onClick={onLogout}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
            Use a different account
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Onboarding View ───────────────────────────────────────────────────────────

function OnboardingView({ session, onFarmAdded, onLogout, onBack }) {
  const [name, setName] = useState('');
  const [license, setLicense] = useState('');
  const [userKey, setUserKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const farm = await api('POST', '/farms/', {
        account_id: session.account_id,
        name,
        license,
        user_key: userKey,
      });
      onFarmAdded(farm);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ width: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12, textDecoration: 'underline', marginBottom: 16 }}>← TerraSoft</button>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6 }}>
            TerraLink <span style={{ color: C.accent }}>Admin</span>
          </div>
          <div style={{ color: C.text, fontWeight: 600, fontSize: 16, marginBottom: 4 }}>Add your first farm</div>
          <div style={{ color: C.muted, fontSize: 13 }}>Connect your METRC license to get started.</div>
          <button onClick={onLogout} style={{ marginTop: 10, background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>Sign out</button>
        </div>

        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 28 }}>
          <form onSubmit={submit}>
            <div style={{ marginBottom: 16 }}>
              <label style={s.label}>Farm Name</label>
              <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="Green Valley Farm" required />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={s.label}>METRC License Number</label>
              <input style={s.input} value={license} onChange={e => setLicense(e.target.value)} placeholder="SF-SBX-OR-1-00000" required />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={s.label}>METRC User API Key</label>
              <input style={s.input} value={userKey} onChange={e => setUserKey(e.target.value)} placeholder="Paste your METRC user key" required />
            </div>
            <div style={{ marginBottom: 20, background: '#0a1c24', border: `1px solid ${C.border}`, borderRadius: 8, padding: '14px 16px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, marginBottom: 8 }}>Before you paste your key</div>
              <ul style={{ fontSize: 12, color: C.muted, lineHeight: 1.8, paddingLeft: 16, margin: 0 }}>
                <li>Make sure you are logged into METRC under the correct farm license</li>
                <li>Your API key inherits your account's permissions — ensure your account has the access TerraLink needs (packages, transfers, adjustments)</li>
                <li>Never share this key with anyone. It grants API access to your METRC account</li>
                <li>Find your key in the METRC portal under <strong style={{ color: C.text }}>Admin → API Keys</strong></li>
              </ul>
            </div>

            {error && <div style={{ marginBottom: 16 }}><Alert>{error}</Alert></div>}

            <button type="submit" disabled={loading} style={{ ...s.btn('primary'), width: '100%', opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Connecting…' : 'Connect Farm'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Workers Tab ───────────────────────────────────────────────────────────────

function WorkersTab({ session, activeFarm }) {
  const [workers, setWorkers]                   = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [showAdd, setShowAdd]                   = useState(false);
  const [permWorker, setPermWorker]             = useState(null);
  const [assignRolesWorker, setAssignRolesWorker] = useState(null);
  const [showRolesManager, setShowRolesManager] = useState(false);
  const [error, setError]                       = useState('');

  async function load() {
    setLoading(true);
    try {
      const data = await api('GET', `/workers/?farm_license=${encodeURIComponent(activeFarm.license)}`);
      setWorkers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [activeFarm.license]);

  async function resetPassword(worker) {
    if (!confirm(`Reset password for ${worker.name}? They will need to set a new password on next login.`)) return;
    try {
      await api('PATCH', `/workers/${worker.id}/reset-password`, { account_id: session.account_id });
      setWorkers(ws => ws.map(w => w.id === worker.id ? { ...w, has_set_password: false } : w));
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteWorker(worker) {
    if (!confirm(`Delete ${worker.name}? This cannot be undone.`)) return;
    try {
      await api('DELETE', `/workers/${worker.id}?account_id=${session.account_id}`);
      setWorkers(ws => ws.filter(w => w.id !== worker.id));
    } catch (err) {
      setError(err.message);
    }
  }

  function handleRolesSaved(worker, result) {
    setWorkers(ws => ws.map(w =>
      w.id === worker.id ? { ...w, is_owner: result.is_owner, roles: result.roles } : w
    ));
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button onClick={() => setShowAdd(true)} style={s.btn('primary')}>+ Add Worker</button>
        <button onClick={() => setShowRolesManager(true)} style={s.btn('ghost')}>Manage Roles</button>
      </div>

      {error && <div style={{ marginBottom: 16 }}><Alert>{error}</Alert></div>}

      {loading ? <Spinner /> : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '200px 150px 1fr auto',
            gap: 12, padding: '10px 16px',
            background: C.card, borderBottom: `1px solid ${C.border}`,
            fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            <span>Name</span><span>Username</span><span>Roles</span><span>Actions</span>
          </div>

          {workers.length === 0 && (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: C.muted, fontSize: 14 }}>
              No workers yet. Add your first worker to get started.
            </div>
          )}

          {workers.map((w, i) => (
            <div key={w.id} style={{
              display: 'grid', gridTemplateColumns: '200px 150px 1fr auto',
              gap: 12, padding: '12px 16px', alignItems: 'center',
              borderBottom: i < workers.length - 1 ? `1px solid ${C.border2}` : 'none',
              background: i % 2 === 0 ? 'transparent' : '#0a1c2410',
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{w.name}</div>
                <div style={{ marginTop: 3 }}>
                  {w.has_set_password
                    ? <Chip label="password set" color={C.green} />
                    : <Chip label="no password" color={C.amber} />}
                </div>
              </div>

              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 12, color: C.muted }}>{w.username}</div>

              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                {(w.roles || []).map(r => (
                  <Chip key={r.id} label={r.name} color={r.is_system ? C.gold : C.accent} />
                ))}
                {(!w.roles || w.roles.length === 0) && (
                  <span style={{ fontSize: 12, color: C.muted }}>No roles</span>
                )}
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button onClick={() => setAssignRolesWorker(w)}
                  style={{ ...s.btn('ghost'), padding: '5px 10px', fontSize: 12 }}>
                  Roles
                </button>
                <button onClick={() => setPermWorker(w)}
                  style={{ ...s.btn('ghost'), padding: '5px 10px', fontSize: 12 }}>
                  Permissions
                </button>
                <button onClick={() => resetPassword(w)}
                  style={{ ...s.btn('ghost'), padding: '5px 10px', fontSize: 12 }}>
                  Reset PW
                </button>
                <button onClick={() => deleteWorker(w)}
                  style={{ ...s.btn('danger'), padding: '5px 10px', fontSize: 12 }}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddWorkerModal
          session={session} activeFarm={activeFarm}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); load(); }}
        />
      )}

      {assignRolesWorker && (
        <AssignRolesModal
          worker={assignRolesWorker} session={session} activeFarm={activeFarm}
          onClose={() => setAssignRolesWorker(null)}
          onSaved={result => { handleRolesSaved(assignRolesWorker, result); setAssignRolesWorker(null); }}
        />
      )}

      {permWorker && (
        <PermissionsModal
          worker={permWorker} session={session} activeFarm={activeFarm}
          onClose={() => setPermWorker(null)}
        />
      )}

      {showRolesManager && (
        <RolesManagerModal
          session={session} activeFarm={activeFarm}
          onClose={() => { setShowRolesManager(false); load(); }}
        />
      )}
    </div>
  );
}

function AddWorkerModal({ session, activeFarm, onClose, onAdded }) {
  const [name, setName]       = useState('');
  const [username, setUsername] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api('POST', '/workers/admin/create', {
        account_id: session.account_id,
        farm_license: activeFarm.license,
        name,
        username,
      });
      onAdded();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title="Add Worker" onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ marginBottom: 14 }}>
          <label style={s.label}>Full Name</label>
          <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" required autoFocus />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={s.label}>Username</label>
          <input style={s.input} value={username} onChange={e => setUsername(e.target.value)} placeholder="Used to log in to TerraLink" required minLength={2} />
        </div>
        <div style={{ marginBottom: 16, fontSize: 12, color: C.muted }}>
          Worker sets their own password on first login. Assign roles after creation.
        </div>
        {error && <div style={{ marginBottom: 14 }}><Alert>{error}</Alert></div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose} style={s.btn('ghost')}>Cancel</button>
          <button type="submit" disabled={loading} style={{ ...s.btn('primary'), opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Adding…' : 'Add Worker'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Assign Roles Modal ────────────────────────────────────────────────────────

function AssignRolesModal({ worker, session, activeFarm, onClose, onSaved }) {
  const [roles, setRoles]       = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  useEffect(() => {
    Promise.all([
      api('GET', `/roles/?farm_license=${encodeURIComponent(activeFarm.license)}&account_id=${session.account_id}`),
      api('GET', `/workers/${worker.id}/permissions?farm_license=${encodeURIComponent(activeFarm.license)}`),
    ]).then(([rolesData, permsData]) => {
      setRoles(rolesData);
      setSelected(new Set((permsData.roles || []).map(r => r.id)));
    }).catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, []);

  async function save() {
    setSaving(true); setErr('');
    try {
      const result = await api('PUT', `/workers/${worker.id}/assign-roles`, {
        account_id: session.account_id,
        role_ids: [...selected],
      });
      onSaved(result);
    } catch (e) { setErr(e.message); setSaving(false); }
  }

  return (
    <Modal title={`Assign Roles — ${worker.name}`} onClose={onClose}>
      {loading ? <Spinner /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {roles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: C.muted, fontSize: 14 }}>
              No roles yet. Create some with "Manage Roles".
            </div>
          ) : roles.map(role => {
            const on = selected.has(role.id);
            return (
              <label key={role.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 6, cursor: 'pointer',
                background: on ? C.accent + '12' : C.bg,
                border: `1px solid ${on ? C.accent + '40' : C.border}`,
              }}>
                <input
                  type="checkbox" checked={on}
                  onChange={() => setSelected(prev => {
                    const next = new Set(prev);
                    if (next.has(role.id)) next.delete(role.id); else next.add(role.id);
                    return next;
                  })}
                  style={{ accentColor: C.accent, width: 14, height: 14 }}
                />
                <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{role.name}</span>
                {role.is_system && <Chip label="System" color={C.gold} />}
                {!role.is_system && role.permissions.length > 0 && (
                  <span style={{ fontSize: 11, color: C.muted }}>{role.permissions.length} perms</span>
                )}
              </label>
            );
          })}
          {err && <Alert>{err}</Alert>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
            <button onClick={onClose} style={s.btn('ghost')}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ ...s.btn('primary'), opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Roles Manager Modal ───────────────────────────────────────────────────────

const PERM_GROUPS_GRANTABLE = PERM_GROUPS.filter(g => !g.locked);

function RolesManagerModal({ session, activeFarm, onClose }) {
  const [roles, setRoles]       = useState(null);
  const [editingId, setEditingId] = useState(null); // role id or 'new'
  const [editName, setEditName] = useState('');
  const [editPerms, setEditPerms] = useState(new Set());
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  function load() {
    setRoles(null);
    api('GET', `/roles/?farm_license=${encodeURIComponent(activeFarm.license)}&account_id=${session.account_id}`)
      .then(setRoles)
      .catch(e => setErr(e.message));
  }
  useEffect(load, []);

  function startEdit(role) {
    setEditingId(role.id); setEditName(role.name);
    setEditPerms(new Set(role.permissions)); setErr('');
  }
  function startCreate() {
    setEditingId('new'); setEditName(''); setEditPerms(new Set()); setErr('');
  }
  function cancelEdit() { setEditingId(null); setErr(''); }

  function togglePerm(key) {
    setEditPerms(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }

  async function saveEdit() {
    if (!editName.trim()) return;
    setSaving(true); setErr('');
    try {
      if (editingId === 'new') {
        await api('POST', '/roles/', {
          account_id: session.account_id,
          farm_license: activeFarm.license,
          name: editName,
          permissions: [...editPerms],
        });
      } else {
        await api('PATCH', `/roles/${editingId}`, {
          account_id: session.account_id,
          name: editName,
          permissions: [...editPerms],
        });
      }
      setEditingId(null); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  async function deleteRole(id) {
    if (!confirm('Delete this role? Workers assigned to it will lose its permissions.')) return;
    try { await api('DELETE', `/roles/${id}?account_id=${session.account_id}`); load(); }
    catch (e) { setErr(e.message); }
  }

  const permEditor = (
    <div style={{ paddingTop: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <label style={s.label}>Role Name</label>
        <input style={s.input} value={editName} onChange={e => setEditName(e.target.value)}
          placeholder="e.g. Harvest Lead" autoFocus={editingId === 'new'} />
      </div>
      {PERM_GROUPS_GRANTABLE.map(group => (
        <div key={group.label} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
            {group.label}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {group.perms.map(p => {
              const on = editPerms.has(p.key);
              return (
                <label key={p.key} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                  background: on ? C.accent + '15' : C.card,
                  border: `1px solid ${on ? C.accent + '50' : C.border}`,
                }}>
                  <input type="checkbox" checked={on} onChange={() => togglePerm(p.key)} style={{ accentColor: C.accent }} />
                  {p.label}
                </label>
              );
            })}
          </div>
        </div>
      ))}
      {err && <div style={{ marginTop: 8 }}><Alert>{err}</Alert></div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button onClick={cancelEdit} style={s.btn('ghost')}>Cancel</button>
        <button onClick={saveEdit} disabled={saving || !editName.trim()}
          style={{ ...s.btn('primary'), opacity: saving || !editName.trim() ? 0.6 : 1 }}>
          {saving ? 'Saving…' : editingId === 'new' ? 'Create Role' : 'Save Role'}
        </button>
      </div>
    </div>
  );

  return (
    <Modal title="Manage Roles" onClose={onClose}>
      <div style={{ width: 500 }}>
        {!roles ? <Spinner /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {roles.map(role => (
              <div key={role.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: C.card }}>
                  <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{role.name}</span>
                  {role.is_system
                    ? <><Chip label="System" color={C.gold} /><span style={{ fontSize: 11, color: C.muted }}>all permissions</span></>
                    : <><span style={{ fontSize: 11, color: C.muted }}>{role.permissions.length} perms</span>
                        {editingId !== role.id && (
                          <>
                            <button onClick={() => startEdit(role)} style={{ ...s.btn('ghost'), padding: '4px 8px', fontSize: 12 }}>Edit</button>
                            <button onClick={() => deleteRole(role.id)} style={{ ...s.btn('danger'), padding: '4px 8px', fontSize: 12 }}>Delete</button>
                          </>
                        )}
                      </>
                  }
                </div>
                {editingId === role.id && (
                  <div style={{ padding: '0 14px 14px', borderTop: `1px solid ${C.border}` }}>
                    {permEditor}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {editingId === 'new' ? (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 14 }}>
            {permEditor}
          </div>
        ) : (
          <button onClick={startCreate} style={{ ...s.btn('ghost'), width: '100%' }}>+ New Role</button>
        )}
      </div>
    </Modal>
  );
}

// ── Stats Tab ─────────────────────────────────────────────────────────────────

const OP_META = {
  adjustment: { label: 'Adjustments',     color: C.amber },
  combine:  { label: 'Combines',          color: C.accent },
  create:   { label: 'New Packages',      color: C.green },
  location: { label: 'Location Changes',  color: C.blue },
};

function StatsTab({ session, activeFarm }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api('GET', `/farms/${encodeURIComponent(activeFarm.license)}/stats?account_id=${session.account_id}`)
      .then(data => { setStats(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [activeFarm.license]);

  if (loading) return <Spinner />;
  if (error) return <Alert>{error}</Alert>;
  if (!stats) return null;

  const { recent, ...counts } = stats;

  return (
    <div>
      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        {Object.entries(counts).map(([type, data]) => {
          const meta = OP_META[type] || { label: type, color: C.muted };
          return (
            <div key={type} style={{
              background: C.panel, border: `1px solid ${C.border}`,
              borderRadius: 8, padding: 18,
              borderTop: `3px solid ${meta.color}`,
            }}>
              <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 10 }}>{meta.label}</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: meta.color, letterSpacing: '-0.03em' }}>{data.total}</div>
              <div style={{ marginTop: 8, fontSize: 12, color: C.muted }}>
                <span style={{ color: C.text }}>{data.last_7_days}</span> last 7 days &nbsp;·&nbsp;
                <span style={{ color: C.text }}>{data.last_30_days}</span> last 30
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent operations */}
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Recent Operations</div>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '120px 110px 140px 1fr auto',
          gap: 12, padding: '10px 16px',
          background: C.card, borderBottom: `1px solid ${C.border}`,
          fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          <span>Type</span><span>Status</span><span>Worker</span><span>Package</span><span>Date</span>
        </div>
        {recent.length === 0 && (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: C.muted, fontSize: 14 }}>
            No operations recorded yet.
          </div>
        )}
        {recent.map((op, i) => {
          const meta = OP_META[op.type] || { color: C.muted };
          const date = op.created_at ? new Date(op.created_at).toLocaleDateString() : '—';
          return (
            <div key={op.id} style={{
              display: 'grid', gridTemplateColumns: '120px 110px 140px 1fr auto',
              gap: 12, padding: '10px 16px', alignItems: 'center', fontSize: 13,
              borderBottom: i < recent.length - 1 ? `1px solid ${C.border2}` : 'none',
            }}>
              <Chip label={op.type} color={meta.color} />
              <Chip label={op.status} color={op.status === 'synced' ? C.green : op.status === 'failed' ? C.red : C.amber} />
              <span style={{ color: C.muted }}>{op.worker_name || '—'}</span>
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: 12, color: C.muted }}>{op.package_label || '—'}</span>
              <span style={{ color: C.muted, fontSize: 12 }}>{date}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Devices Tab ───────────────────────────────────────────────────────────────

function DevicesTab({ session, activeFarm }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  function load() {
    setLoading(true);
    api('GET', `/devices/?farm_license=${encodeURIComponent(activeFarm.license)}&account_id=${session.account_id}`)
      .then(data => { setDevices(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }

  useEffect(load, [activeFarm.license]);

  async function revoke(id) {
    try {
      await api('DELETE', `/devices/${id}?account_id=${session.account_id}`);
      setDevices(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      alert(e.message);
    }
  }

  if (loading) return <Spinner />;
  if (error)   return <Alert>{error}</Alert>;

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Trusted Devices</div>
      {devices.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 14, padding: '32px 0', textAlign: 'center' }}>
          No trusted devices yet. Devices are added the first time a worker logs in from a new machine.
        </div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 120px 130px 130px 80px',
            gap: 12, padding: '10px 16px',
            background: C.card, borderBottom: `1px solid ${C.border}`,
            fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            <span>Device</span><span>ID Type</span><span>First Seen</span><span>Last Seen</span><span></span>
          </div>
          {devices.map((d, i) => (
            <div key={d.id} style={{
              display: 'grid', gridTemplateColumns: '1fr 120px 130px 130px 80px',
              gap: 12, padding: '12px 16px', alignItems: 'center', fontSize: 13,
              borderBottom: i < devices.length - 1 ? `1px solid ${C.border2}` : 'none',
            }}>
              <div>
                <div style={{ fontWeight: 600 }}>{d.hostname || 'Unknown device'}</div>
                {d.os_user && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{d.os_user}</div>}
              </div>
              <Chip
                label={d.id_method === 'hardware' ? 'Hardware' : 'UUID'}
                color={d.id_method === 'hardware' ? C.green : C.amber}
              />
              <span style={{ fontSize: 12, color: C.muted }}>
                {d.first_seen ? new Date(d.first_seen).toLocaleDateString() : '—'}
              </span>
              <span style={{ fontSize: 12, color: C.muted }}>
                {d.last_seen ? new Date(d.last_seen).toLocaleDateString() : '—'}
              </span>
              <button onClick={() => revoke(d.id)} style={{ ...s.btn('danger'), padding: '5px 10px', fontSize: 12 }}>
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Add Farm Modal ────────────────────────────────────────────────────────────

function AddFarmModal({ session, onClose, onAdded }) {
  const [name, setName] = useState('');
  const [license, setLicense] = useState('');
  const [userKey, setUserKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const farm = await api('POST', '/farms/', { account_id: session.account_id, name, license, user_key: userKey });
      onAdded(farm);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title="Add Farm" onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ marginBottom: 14 }}>
          <label style={s.label}>Farm Name</label>
          <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="Green Valley Farm" required autoFocus />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={s.label}>METRC License Number</label>
          <input style={s.input} value={license} onChange={e => setLicense(e.target.value)} placeholder="SF-SBX-OR-1-00000" required />
        </div>
        <div style={{ marginBottom: 8 }}>
          <label style={s.label}>METRC User API Key</label>
          <input style={s.input} value={userKey} onChange={e => setUserKey(e.target.value)} placeholder="Paste your METRC user key" required />
        </div>
        <div style={{ marginBottom: 16, background: '#0a1c24', border: `1px solid ${C.border}`, borderRadius: 8, padding: '14px 16px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, marginBottom: 8 }}>Before you paste your key</div>
          <ul style={{ fontSize: 12, color: C.muted, lineHeight: 1.8, paddingLeft: 16, margin: 0 }}>
            <li>Make sure you are logged into METRC under the correct farm license</li>
            <li>Your API key inherits your account's permissions — ensure your account has the access TerraLink needs (packages, transfers, adjustments)</li>
            <li>Never share this key with anyone. It grants API access to your METRC account</li>
            <li>Find your key in the METRC portal under <strong style={{ color: C.text }}>Admin → API Keys</strong></li>
          </ul>
        </div>
        {error && <div style={{ marginBottom: 14 }}><Alert>{error}</Alert></div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose} style={s.btn('ghost')}>Cancel</button>
          <button type="submit" disabled={loading} style={{ ...s.btn('primary'), opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Connecting…' : 'Connect Farm'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Support ───────────────────────────────────────────────────────────────────

const TICKET_STATUS_COLORS = {
  open:        C.accent,
  in_progress: C.blue,
  resolved:    C.green,
  closed:      C.muted,
};

const CATEGORY_LABELS = {
  bug:      'Bug',
  question: 'Question',
  billing:  'Billing',
  feature:  'Feature Request',
  other:    'Other',
};

function SupportTab({ session }) {
  const [view,    setView]    = useState('list');
  const [tickets, setTickets] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew]  = useState(false);
  const [err,     setErr]      = useState('');

  function load() {
    api('GET', `/support/tickets?account_id=${session.account_id}`)
      .then(setTickets)
      .catch(e => setErr(e.message));
  }
  useEffect(load, []);

  function openTicket(id) {
    api('GET', `/support/tickets/${id}?account_id=${session.account_id}`)
      .then(t => { setSelected(t); setView('detail'); })
      .catch(e => setErr(e.message));
  }

  if (view === 'detail' && selected) {
    return (
      <TicketDetail
        ticket={selected}
        session={session}
        onBack={() => { setView('list'); load(); setSelected(null); }}
        onUpdate={setSelected}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 18 }}>Support</div>
        <button onClick={() => setShowNew(true)} style={s.btn('primary')}>Open a Ticket</button>
      </div>

      {err && <div style={{ marginBottom: 12 }}><Alert>{err}</Alert></div>}

      {!tickets ? <Spinner /> : tickets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: C.muted, fontSize: 14 }}>
          No tickets yet. Hit "Open a Ticket" if you need help.
        </div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 140px 110px 130px',
            padding: '10px 16px', background: C.card, borderBottom: `1px solid ${C.border}`,
            fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            <span>Subject</span><span>Category</span><span>Status</span><span>Opened</span>
          </div>
          {tickets.map((t, i) => (
            <div
              key={t.id}
              onClick={() => openTicket(t.id)}
              style={{
                display: 'grid', gridTemplateColumns: '1fr 140px 110px 130px',
                padding: '12px 16px', alignItems: 'center', fontSize: 13, cursor: 'pointer',
                borderBottom: i < tickets.length - 1 ? `1px solid ${C.border2}` : 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#0d253540'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontWeight: 600 }}>{t.subject}</span>
              <Chip label={CATEGORY_LABELS[t.category] || t.category} color={C.blue} />
              <Chip label={t.status.replace('_', ' ')} color={TICKET_STATUS_COLORS[t.status] || C.muted} />
              <span style={{ fontSize: 12, color: C.muted }}>{new Date(t.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewTicketModal
          session={session}
          onClose={() => setShowNew(false)}
          onCreated={id => { setShowNew(false); load(); openTicket(id); }}
        />
      )}
    </div>
  );
}

async function doUpload(files, session, pending, setPending, setUploading) {
  if (!files.length) return;
  if (pending.length + files.length > 5) { alert('Maximum 5 files per message.'); return; }
  setUploading(true);
  try {
    const fd = new FormData();
    fd.append('account_id', session.account_id);
    files.forEach(f => fd.append('files', f));
    const res = await fetch('/support/upload', { method: 'POST', body: fd });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Upload failed');
    setPending(prev => [...prev, ...data]);
  } catch (e) { alert(e.message); }
  finally { setUploading(false); }
}

function makePasteHandler(session, pending, setPending, setUploading) {
  return async function handlePaste(e) {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItems = items.filter(i => i.type.startsWith('image/'));
    if (!imageItems.length) return;
    e.preventDefault();
    const files = imageItems.map((item, i) => {
      const blob = item.getAsFile();
      const ext = item.type === 'image/png' ? '.png' : item.type === 'image/jpeg' ? '.jpg' : '.png';
      return new File([blob], `screenshot-${Date.now()}-${i}${ext}`, { type: item.type });
    });
    await doUpload(files, session, pending, setPending, setUploading);
  };
}

function FilePicker({ session, pending, setPending, uploading, setUploading }) {
  const inputRef = useRef(null);

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    await doUpload(files, session, pending, setPending, setUploading);
    e.target.value = '';
  }

  function remove(id) { setPending(prev => prev.filter(a => a.id !== id)); }

  function fmt(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <div style={{ marginTop: 10 }}>
      <input ref={inputRef} type="file" multiple onChange={handleFiles} style={{ display: 'none' }} />
      <button
        type="button"
        onClick={() => inputRef.current.click()}
        disabled={uploading || pending.length >= 5}
        style={{ ...s.btn('ghost'), padding: '6px 12px', fontSize: 12, opacity: uploading || pending.length >= 5 ? 0.5 : 1 }}
      >
        {uploading ? 'Uploading…' : '+ Attach File'}
      </button>
      {pending.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {pending.map(a => (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 6, padding: '4px 8px', fontSize: 12,
            }}>
              <span style={{ color: C.text, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.original_name}</span>
              <span style={{ color: C.muted }}>{fmt(a.size)}</span>
              <button type="button" onClick={() => remove(a.id)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentRow({ att }) {
  const isImage = att.mime_type && att.mime_type.startsWith('image/');
  return isImage ? (
    <a href={att.url} target="_blank" rel="noopener" style={{ display: 'inline-block', marginTop: 8 }}>
      <img src={att.url} alt={att.original_name} style={{ maxWidth: 240, maxHeight: 160, borderRadius: 6, border: `1px solid ${C.border}`, display: 'block' }} />
    </a>
  ) : (
    <a href={att.url} download={att.original_name} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6,
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 6, padding: '5px 10px', fontSize: 12, color: C.text, textDecoration: 'none',
    }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
      {att.original_name}
    </a>
  );
}

function NewTicketModal({ session, onClose, onCreated }) {
  const [subject,    setSubject]    = useState('');
  const [category,   setCategory]   = useState('other');
  const [message,    setMessage]    = useState('');
  const [loading,    setLoading]    = useState(false);
  const [err,        setErr]        = useState('');
  const [pending,    setPending]    = useState([]);
  const [uploading,  setUploading]  = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      const res = await api('POST', '/support/tickets', {
        account_id: session.account_id, subject, category, message,
        attachment_ids: pending.map(a => a.id),
      });
      onCreated(res.id);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  return (
    <Modal title="Open a Ticket" onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ marginBottom: 14 }}>
          <label style={s.label}>Subject</label>
          <input style={s.input} value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="Brief description of your issue" required autoFocus />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={s.label}>Category</label>
          <select style={s.input} value={category} onChange={e => setCategory(e.target.value)}>
            <option value="bug">Bug</option>
            <option value="question">Question</option>
            <option value="billing">Billing</option>
            <option value="feature">Feature Request</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div style={{ marginBottom: 6 }}>
          <label style={s.label}>Message</label>
          <textarea
            style={{ ...s.input, minHeight: 120, resize: 'vertical' }}
            value={message} onChange={e => setMessage(e.target.value)}
            onPaste={makePasteHandler(session, pending, setPending, setUploading)}
            placeholder="Describe your issue in detail..." required
          />
        </div>
        <FilePicker session={session} pending={pending} setPending={setPending} uploading={uploading} setUploading={setUploading} />
        {err && <div style={{ margin: '12px 0' }}><Alert>{err}</Alert></div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
          <button type="button" onClick={onClose} style={s.btn('ghost')}>Cancel</button>
          <button type="submit" disabled={loading || uploading} style={{ ...s.btn('primary'), opacity: loading || uploading ? 0.6 : 1 }}>
            {loading ? 'Submitting…' : 'Submit Ticket'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TicketDetail({ ticket, session, onBack, onUpdate }) {
  const [reply,     setReply]     = useState('');
  const [sending,   setSending]   = useState(false);
  const [err,       setErr]       = useState('');
  const [pending,   setPending]   = useState([]);
  const [uploading, setUploading] = useState(false);

  async function sendReply(e) {
    e.preventDefault();
    if (!reply.trim() && pending.length === 0) return;
    setSending(true); setErr('');
    try {
      await api('POST', `/support/tickets/${ticket.id}/messages`, {
        account_id: session.account_id, body: reply,
        attachment_ids: pending.map(a => a.id),
      });
      const updated = await api('GET', `/support/tickets/${ticket.id}?account_id=${session.account_id}`);
      onUpdate(updated);
      setReply('');
      setPending([]);
    } catch (e) { setErr(e.message); }
    finally { setSending(false); }
  }

  const isClosed = ticket.status === 'closed';

  return (
    <div>
      <button
        onClick={onBack}
        style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 20 }}
      >
        ← Back to Support
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>{ticket.subject}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Chip label={CATEGORY_LABELS[ticket.category] || ticket.category} color={C.blue} />
            <Chip label={ticket.status.replace('_', ' ')} color={TICKET_STATUS_COLORS[ticket.status] || C.muted} />
            <Chip
              label={ticket.priority}
              color={ticket.priority === 'urgent' ? C.red : ticket.priority === 'high' ? C.amber : C.muted}
            />
          </div>
        </div>
        <span style={{ fontSize: 12, color: C.muted }}>
          #{ticket.id} · {new Date(ticket.created_at).toLocaleDateString()}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {ticket.messages.map(m => {
          const isUser = m.sender_type === 'user';
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '72%',
                background: isUser ? C.accent + '22' : C.panel,
                border: `1px solid ${isUser ? C.accent + '44' : C.border}`,
                borderRadius: 10, padding: '12px 16px',
              }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
                  {isUser ? 'You' : 'TerraSoft Support'} · {new Date(m.created_at).toLocaleString()}
                </div>
                {m.body && <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.body}</div>}
                {m.attachments && m.attachments.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: m.body ? 8 : 0 }}>
                    {m.attachments.map(a => <AttachmentRow key={a.id} att={a} />)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {err && <div style={{ marginBottom: 12 }}><Alert>{err}</Alert></div>}

      {isClosed ? (
        <div style={{
          textAlign: 'center', padding: 20, color: C.muted, fontSize: 14,
          border: `1px solid ${C.border}`, borderRadius: 8,
        }}>
          This ticket is closed.
        </div>
      ) : (
        <form onSubmit={sendReply}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <textarea
              style={{ ...s.input, flex: 1, minHeight: 80, resize: 'vertical' }}
              value={reply} onChange={e => setReply(e.target.value)}
              onPaste={makePasteHandler(session, pending, setPending, setUploading)}
              placeholder="Write a reply..."
            />
            <button
              type="submit"
              disabled={sending || uploading || (!reply.trim() && pending.length === 0)}
              style={{ ...s.btn('primary'), opacity: sending || uploading || (!reply.trim() && pending.length === 0) ? 0.6 : 1, whiteSpace: 'nowrap' }}
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
          <FilePicker session={session} pending={pending} setPending={setPending} uploading={uploading} setUploading={setUploading} />
        </form>
      )}
    </div>
  );
}

// ── Account View ─────────────────────────────────────────────────────────────

function AccountView({ session, onBack, onLogout, onEmailChanged }) {
  const [summary, setSummary]         = useState(null);
  const [summaryErr, setSummaryErr]   = useState('');

  // Verification resend
  const [resending,   setResending]   = useState(false);
  const [resendSent,  setResendSent]  = useState(false);
  const [resendErr,   setResendErr]   = useState('');

  // Change password
  const [curPw,  setCurPw]   = useState('');
  const [newPw,  setNewPw]   = useState('');
  const [confPw, setConfPw]  = useState('');
  const [pwMsg,  setPwMsg]   = useState(null);
  const [pwBusy, setPwBusy]  = useState(false);

  // Change email
  const [newEmail,    setNewEmail]    = useState('');
  const [emailPw,     setEmailPw]     = useState('');
  const [emailMsg,    setEmailMsg]    = useState(null);
  const [emailBusy,   setEmailBusy]   = useState(false);

  async function resendVerification() {
    setResending(true); setResendErr(''); setResendSent(false);
    try {
      await api('POST', '/auth/resend-verification', { account_id: session.account_id });
      setResendSent(true);
    } catch (err) { setResendErr(err.message); }
    finally { setResending(false); }
  }

  // Cancel
  const [showCancel,  setShowCancel]  = useState(false);
  const [cancelBusy,  setCancelBusy]  = useState(false);
  const [cancelDone,  setCancelDone]  = useState(false);

  useEffect(() => {
    api('GET', `/auth/account-summary?account_id=${session.account_id}`)
      .then(setSummary)
      .catch(e => setSummaryErr(e.message));
  }, []);

  async function changePassword(e) {
    e.preventDefault();
    if (newPw !== confPw) { setPwMsg({ type: 'error', text: 'Passwords do not match.' }); return; }
    setPwBusy(true); setPwMsg(null);
    try {
      await api('PATCH', '/auth/change-password', { account_id: session.account_id, current_password: curPw, new_password: newPw });
      setPwMsg({ type: 'ok', text: 'Password updated.' });
      setCurPw(''); setNewPw(''); setConfPw('');
    } catch (err) { setPwMsg({ type: 'error', text: err.message }); }
    finally { setPwBusy(false); }
  }

  async function changeEmail(e) {
    e.preventDefault();
    setEmailBusy(true); setEmailMsg(null);
    try {
      await api('PATCH', '/auth/change-email', { account_id: session.account_id, current_password: emailPw, new_email: newEmail });
      setEmailMsg({ type: 'ok', text: 'Verification email sent. Check your inbox, then sign back in.' });
      setTimeout(() => onEmailChanged(), 3000);
    } catch (err) { setEmailMsg({ type: 'error', text: err.message }); }
    finally { setEmailBusy(false); }
  }

  async function requestCancel() {
    setCancelBusy(true);
    try {
      await api('POST', '/support/tickets', {
        account_id: session.account_id,
        subject: 'Cancel my subscription',
        category: 'billing',
        message: 'I would like to cancel my TerraSoft subscription. Please process my cancellation request.',
        attachment_ids: [],
      });
      setCancelDone(true);
    } catch (err) { alert(err.message); }
    finally { setCancelBusy(false); setShowCancel(false); }
  }

  const eventsLimit = 500; // placeholder until pricing is finalized
  const eventsUsed  = summary?.events_this_month || 0;
  const eventsPct   = Math.min(100, Math.round((eventsUsed / eventsLimit) * 100));

  const section = (title) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
      {title}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: C.bg }}>
      <div style={{
        height: 56, background: C.panel, borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', padding: '0 24px', gap: 16, flexShrink: 0,
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12, padding: 0 }}>← TerraSoft</button>
        <div style={{ width: 1, height: 16, background: C.border }} />
        <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em' }}>
          Terra<span style={{ color: C.accent }}>Soft</span> <span style={{ color: C.muted, fontWeight: 500 }}>Account</span>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: C.muted }}>{session.email}</span>
        <button onClick={onLogout} style={{ ...s.btn('ghost'), padding: '6px 12px', fontSize: 12 }}>Sign Out</button>
      </div>

      <div className="scroll" style={{ flex: 1, padding: 32 }}>
        <div style={{ maxWidth: 860, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>

          {/* ── Security ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* Change Password */}
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 24 }}>
              {section('Security')}

              {/* Email verification status */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${C.border}` }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Email Verification</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{session.email}</div>
                </div>
                {session.is_verified ? (
                  <Chip label="Verified" color={C.green} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    <Chip label="Unverified" color={C.amber} />
                    <button
                      onClick={resendVerification}
                      disabled={resending}
                      style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, padding: 0, opacity: resending ? 0.6 : 1 }}
                    >
                      {resending ? 'Sending…' : 'Resend verification email'}
                    </button>
                    {resendSent && <span style={{ fontSize: 11, color: C.accent }}>Email sent. Check your inbox.</span>}
                    {resendErr && <span style={{ fontSize: 11, color: C.red }}>{resendErr}</span>}
                  </div>
                )}
              </div>

              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Change Password</div>
              <form onSubmit={changePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={s.label}>Current Password</label>
                  <input type="password" style={s.input} value={curPw} onChange={e => setCurPw(e.target.value)} required />
                </div>
                <div>
                  <label style={s.label}>New Password</label>
                  <input type="password" style={s.input} value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={8} />
                </div>
                <div>
                  <label style={s.label}>Confirm New Password</label>
                  <input type="password" style={s.input} value={confPw} onChange={e => setConfPw(e.target.value)} required minLength={8} />
                </div>
                {pwMsg && <Alert type={pwMsg.type === 'ok' ? 'warning' : 'error'}>{pwMsg.text}</Alert>}
                <button type="submit" disabled={pwBusy} style={{ ...s.btn('primary'), opacity: pwBusy ? 0.6 : 1 }}>
                  {pwBusy ? 'Saving…' : 'Update Password'}
                </button>
              </form>
            </div>

            {/* Change Email */}
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Change Email</div>
              <form onSubmit={changeEmail} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={s.label}>New Email</label>
                  <input type="email" autoComplete="off" style={s.input} value={newEmail} onChange={e => setNewEmail(e.target.value)} required />
                </div>
                <div>
                  <label style={s.label}>Current Password</label>
                  <input type="password" autoComplete="current-password" style={s.input} value={emailPw} onChange={e => setEmailPw(e.target.value)} required />
                </div>
                {emailMsg && <Alert type={emailMsg.type === 'ok' ? 'warning' : 'error'}>{emailMsg.text}</Alert>}
                <button type="submit" disabled={emailBusy} style={{ ...s.btn('primary'), opacity: emailBusy ? 0.6 : 1 }}>
                  {emailBusy ? 'Sending…' : 'Change Email'}
                </button>
              </form>
            </div>

          </div>

          {/* ── Subscription ── */}
          <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 24 }}>
            {section('Subscription & Usage')}

            {summaryErr && <Alert>{summaryErr}</Alert>}
            {!summary && !summaryErr && <Spinner />}

            {summary && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Plan */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: C.muted }}>Plan</span>
                  <Chip
                    label={summary.billing_status}
                    color={summary.billing_status === 'active' ? C.green : summary.billing_status === 'overdue' ? C.red : C.muted}
                  />
                </div>

                {/* Farms */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: C.muted }}>Farm Licenses</span>
                    <span style={{ fontSize: 13, color: C.text }}>{summary.farms.length} / {summary.licensed_farms}</span>
                  </div>
                  {summary.farms.map(f => (
                    <div key={f.id} style={{
                      fontSize: 12, color: C.muted, padding: '5px 10px',
                      background: C.bg, borderRadius: 6, marginBottom: 4,
                      fontFamily: 'JetBrains Mono',
                    }}>
                      {f.name} <span style={{ color: C.border, margin: '0 6px' }}>·</span> {f.license}
                    </div>
                  ))}
                  {summary.farms.length === 0 && (
                    <div style={{ fontSize: 12, color: C.muted }}>No farms connected yet.</div>
                  )}
                </div>

                {/* Events bar */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: C.muted }}>Events This Month</span>
                    <span style={{ fontSize: 13, fontFamily: 'JetBrains Mono', color: eventsPct >= 90 ? C.red : C.text }}>
                      {eventsUsed} <span style={{ color: C.muted }}>/ {eventsLimit}</span>
                    </span>
                  </div>
                  <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 3,
                      width: `${eventsPct}%`,
                      background: eventsPct >= 90 ? C.red : eventsPct >= 70 ? C.amber : C.accent,
                      transition: 'width 400ms ease',
                    }} />
                  </div>
                </div>

                {/* Billing */}
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: C.muted }}>Monthly Price</span>
                    <span style={{ fontSize: 13, color: C.text }}>
                      {summary.monthly_price != null ? `$${summary.monthly_price.toFixed(2)}` : '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: C.muted }}>Next Billing Date</span>
                    <span style={{ fontSize: 13, color: C.muted }}>—</span>
                  </div>
                </div>

                {/* Subscribe CTA for inactive accounts */}
                {summary.billing_status === 'inactive' && (
                  <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
                    <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
                      You're on a free account. Subscribe to unlock farm licenses and METRC sync.
                    </div>
                    <a
                      href="/purchase"
                      style={{ ...s.btn('primary'), display: 'block', textAlign: 'center', textDecoration: 'none' }}
                    >
                      View Plans &amp; Subscribe
                    </a>
                  </div>
                )}

                {/* Cancel */}
                {summary.billing_status === 'active' && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
                  {cancelDone ? (
                    <Alert type="warning">Cancellation request submitted. We'll be in touch shortly.</Alert>
                  ) : showCancel ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontSize: 13, color: C.muted }}>This will open a support ticket to cancel your subscription. Are you sure?</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setShowCancel(false)} style={{ ...s.btn('ghost'), flex: 1 }}>Never mind</button>
                        <button onClick={requestCancel} disabled={cancelBusy} style={{ ...s.btn('danger'), flex: 1, opacity: cancelBusy ? 0.6 : 1 }}>
                          {cancelBusy ? 'Submitting…' : 'Yes, cancel'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setShowCancel(true)} style={{ ...s.btn('ghost'), width: '100%', color: C.red, borderColor: C.red + '44' }}>
                      Cancel Subscription
                    </button>
                  )}
                </div>
                )}

              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Update API Key Modal ──────────────────────────────────────────────────────

function UpdateApiKeyModal({ session, farm, onClose }) {
  const [userKey, setUserKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [done, setDone]       = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      await api('PATCH', `/farms/${encodeURIComponent(farm.license)}/user-key`, {
        account_id: session.account_id,
        user_key: userKey,
      });
      setDone(true);
    } catch (err) { setErr(err.message); }
    finally { setLoading(false); }
  }

  return (
    <Modal title={`Update METRC API Key — ${farm.name}`} onClose={onClose}>
      {done ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: '#8ED11B18', border: '1px solid #8ED11B44', borderRadius: 6, padding: '12px 14px', color: C.accent, fontSize: 13 }}>
            API key updated. Package lookups and operations will now use the new key.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={s.btn('primary')}>Done</button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} autoComplete="off">
          <div style={{ marginBottom: 8 }}>
            <label style={s.label}>METRC User API Key</label>
            <input
              style={s.input} value={userKey} onChange={e => setUserKey(e.target.value)}
              placeholder="Paste your production METRC user key" required autoFocus
              autoComplete="off"
            />
          </div>
          <div style={{ marginBottom: 20, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px', fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
            Find your key in the METRC portal under <strong style={{ color: C.text }}>Admin → API Keys</strong>. Make sure you are logged into the correct facility.
          </div>
          {err && <div style={{ marginBottom: 14 }}><Alert>{err}</Alert></div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" onClick={onClose} style={s.btn('ghost')}>Cancel</button>
            <button type="submit" disabled={loading || !userKey.trim()} style={{ ...s.btn('primary'), opacity: loading || !userKey.trim() ? 0.6 : 1 }}>
              {loading ? 'Saving…' : 'Update Key'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function DashboardView({ session, initialFarms, onLogout, onBack }) {
  const [farms, setFarms] = useState(initialFarms);
  const [activeFarm, setActiveFarm] = useState(initialFarms[0] || null);
  const [tab, setTab] = useState('workers');
  const [showAddFarm, setShowAddFarm] = useState(false);
  const [showUpdateKey, setShowUpdateKey] = useState(false);

  function farmAdded(farm) {
    const updated = [...farms, farm];
    setFarms(updated);
    setActiveFarm(farm);
    setShowAddFarm(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '0 24px', height: 56,
        background: C.panel, borderBottom: `1px solid ${C.border}`,
        flexShrink: 0,
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12, padding: 0, marginRight: 4 }}>← TerraSoft</button>
        <div style={{ width: 1, height: 16, background: C.border, marginRight: 12 }} />
        <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em', marginRight: 8 }}>
          TerraLink <span style={{ color: C.accent }}>Admin</span>
        </div>

        {/* Farm selector */}
        <select
          value={activeFarm?.license || ''}
          onChange={e => setActiveFarm(farms.find(f => f.license === e.target.value))}
          style={{ ...s.input, width: 'auto', padding: '6px 10px', fontSize: 13 }}
        >
          {farms.map(f => <option key={f.license} value={f.license}>{f.name}</option>)}
        </select>
        {farms.length < (session.licensed_farms || 0)
          ? <button onClick={() => setShowAddFarm(true)} style={{ ...s.btn('ghost'), padding: '6px 12px', fontSize: 12 }}>+ Farm</button>
          : <span style={{ fontSize: 12, color: C.muted, padding: '6px 12px', border: `1px solid ${C.border}`, borderRadius: 6 }}>
              {farms.length}/{session.licensed_farms} licenses used
            </span>
        }
        {activeFarm && (
          <button onClick={() => setShowUpdateKey(true)} style={{ ...s.btn('ghost'), padding: '6px 12px', fontSize: 12 }}>
            Update API Key
          </button>
        )}

        <div style={{ flex: 1 }} />

        <span style={{ fontSize: 13, color: C.muted }}>{session.email}</span>
        <button onClick={onLogout} style={{ ...s.btn('ghost'), padding: '6px 12px', fontSize: 12 }}>Sign Out</button>
      </div>

      {/* Tabs — always visible */}
      <div style={{ display: 'flex', gap: 2, padding: '0 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        {(activeFarm ? ['workers', 'stats', 'devices', 'support'] : ['support']).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '14px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
            color: tab === t ? C.accent : C.muted,
            borderBottom: tab === t ? `2px solid ${C.accent}` : '2px solid transparent',
            marginBottom: -1,
          }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
        {activeFarm && <>
          <div style={{ flex: 1 }} />
          <div style={{ alignSelf: 'center', fontSize: 12, color: C.muted, fontFamily: 'JetBrains Mono' }}>
            {activeFarm.license}
          </div>
        </>}
      </div>

      {/* Content */}
      <div className="scroll" style={{ flex: 1, padding: 24 }}>
        {tab === 'support' ? (
          <SupportTab session={session} />
        ) : activeFarm ? (
          <>
            {tab === 'workers' && <WorkersTab session={session} activeFarm={activeFarm} />}
            {tab === 'stats'   && <StatsTab   session={session} activeFarm={activeFarm} />}
            {tab === 'devices' && <DevicesTab session={session} activeFarm={activeFarm} />}
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: C.muted }}>
            No farms connected yet.
          </div>
        )}
      </div>

      {showAddFarm && (
        <AddFarmModal session={session} onClose={() => setShowAddFarm(false)} onAdded={farmAdded} />
      )}
      {showUpdateKey && activeFarm && (
        <UpdateApiKeyModal session={session} farm={activeFarm} onClose={() => setShowUpdateKey(false)} />
      )}
    </div>
  );
}

// ── Support View (standalone — accessible without farm/activation) ────────────

function SupportView({ session, onBack, onLogout }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: C.bg }}>
      <div style={{
        height: 56, background: C.panel, borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', padding: '0 24px', gap: 16, flexShrink: 0,
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12, padding: 0 }}>← TerraSoft</button>
        <div style={{ width: 1, height: 16, background: C.border }} />
        <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em' }}>
          Terra<span style={{ color: C.accent }}>Soft</span> <span style={{ color: C.muted, fontWeight: 500 }}>Support</span>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: C.muted }}>{session.email}</span>
        <button onClick={onLogout} style={{ ...s.btn('ghost'), padding: '6px 12px', fontSize: 12 }}>Sign Out</button>
      </div>
      <div className="scroll" style={{ flex: 1, padding: 32, maxWidth: 860, margin: '0 auto', width: '100%' }}>
        <SupportTab session={session} />
      </div>
    </div>
  );
}

// ── Portal View ───────────────────────────────────────────────────────────────

function PortalView({ session, onEnterProduct, onLogout }) {
  const activated = session.licensed_farms > 0;
  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <div style={{
        height: 56, background: C.panel, borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', padding: '0 32px', justifyContent: 'space-between',
      }}>
        <a href="/" style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.03em', color: C.text, textDecoration: 'none' }}>
          Terra<span style={{ color: C.accent }}>Soft</span>
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, color: C.muted }}>{session.email}</span>
          <button onClick={() => onEnterProduct('account')} style={{ ...s.btn('ghost'), padding: '6px 12px', fontSize: 12 }}>Account</button>
          <button onClick={onLogout} style={{ ...s.btn('ghost'), padding: '6px 12px', fontSize: 12 }}>Sign Out</button>
        </div>
      </div>

      <div style={{ padding: '48px 32px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 20 }}>My Products</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>

          {/* TerraLink */}
          <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              <div style={{ fontWeight: 700, fontSize: 16 }}>TerraLink</div>
            </div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 22 }}>
              Cannabis compliance for Oregon producers. Manage farms, workers, and METRC reporting.
            </div>
            {activated ? (
              <button onClick={() => onEnterProduct('terralink')} style={{ ...s.btn('primary'), width: '100%' }}>
                Open TerraLink Admin
              </button>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: C.amber, marginBottom: 12 }}>Not yet activated</div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
                  Contact TerraSoft to activate your subscription. Once activated, sign out and back in to get started.
                </div>
                <button onClick={() => onEnterProduct('support')} style={{ ...s.btn('ghost'), width: '100%' }}>
                  Contact Support
                </button>
              </div>
            )}
          </div>

          {/* Support */}
          <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Support</div>
            </div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 22 }}>
              Questions, issues, or billing inquiries. Open a ticket and we'll get back to you.
            </div>
            <button onClick={() => onEnterProduct('support')} style={{ ...s.btn('ghost'), width: '100%' }}>
              Open Support
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── App Root ──────────────────────────────────────────────────────────────────

function App() {
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tc_session')); } catch { return null; }
  });
  const [farms, setFarms] = useState([]);
  const [loadingFarms, setLoadingFarms] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);

  useEffect(() => {
    if (!session) return;
    // Refresh account data so licensed_farms is always current
    api('GET', `/auth/me?account_id=${session.account_id}`)
      .then(fresh => {
        const updated = { ...session, ...fresh };
        localStorage.setItem('tc_session', JSON.stringify(updated));
        setSession(updated);
      })
      .catch(() => {});
  }, [session?.account_id]);

  useEffect(() => {
    if (!session) return;
    setLoadingFarms(true);
    api('GET', `/farms/?account_id=${session.account_id}`)
      .then(data => {
        setFarms(data);
        setNeedsOnboarding(data.length === 0);
        setLoadingFarms(false);
      })
      .catch(() => setLoadingFarms(false));
  }, [session?.account_id]);

  function handleSession(data, isNew) {
    localStorage.setItem('tc_session', JSON.stringify(data));
    setSession(data);
    if (isNew && data.is_verified) {
      setNeedsOnboarding(true);
    }
  }

  function handleVerified() {
    const updated = { ...session, is_verified: true };
    localStorage.setItem('tc_session', JSON.stringify(updated));
    setSession(updated);
    setNeedsOnboarding(farms.length === 0);
  }

  function handleLogout() {
    localStorage.removeItem('tc_session');
    setSession(null);
    setFarms([]);
    setNeedsOnboarding(false);
    setActiveProduct(null);
  }

  function handleFarmAdded(farm) {
    const updated = [...farms, farm];
    setFarms(updated);
    setNeedsOnboarding(false);
  }

  if (!session) return <AuthView onSession={handleSession} />;
  if (!session.is_verified) return <VerifyEmailView session={session} onVerified={handleVerified} onLogout={handleLogout} />;
  if (loadingFarms) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <Spinner />
    </div>
  );
  if (!activeProduct) return <PortalView session={session} onEnterProduct={setActiveProduct} onLogout={handleLogout} />;
  if (activeProduct === 'support') return <SupportView session={session} onBack={() => setActiveProduct(null)} onLogout={handleLogout} />;
  if (activeProduct === 'account') return <AccountView session={session} onBack={() => setActiveProduct(null)} onLogout={handleLogout} onEmailChanged={handleLogout} />;
  if (needsOnboarding) return <OnboardingView session={session} onFarmAdded={handleFarmAdded} onLogout={handleLogout} onBack={() => setActiveProduct(null)} />;
  return <DashboardView session={session} initialFarms={farms} onLogout={handleLogout} onBack={() => setActiveProduct(null)} />;
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

;